import { useEffect, useState } from "react";
import { ArrowLeft, Cake, CheckCircle2, Loader2 } from "lucide-react";
import { compressImageForUpload, uploadFormWithRetry } from "@/lib/driverImageUpload";
import { appPathname } from "@/lib/opsBase";
import {
  productionAuthBody,
  productionFormAuth,
  productionTaskQuery,
} from "@/lib/productionHubAuth";
import { displayCustomerName } from "@/lib/customerName";
import { whatsappHref } from "@/features/customers/lib/phoneUtils";
import { isNewCustomerOrder } from "@/lib/customerOrderBadge";
import MobileTaskHeader from "@/components/mobile/MobileTaskHeader";
import DecoratorPhotoSlot from "@/components/mobile/DecoratorPhotoSlot";
import FullWidthImageLightbox from "@/components/mobile/FullWidthImageLightbox";
import ProductionCustomerCall from "@/components/ProductionCustomerCall";
import { OrderBakingSpecDisplay } from "@/components/OrderBakingSpecDisplay";
import KitchenTaskScheduleBar, { KITCHEN_TASK_SCHEDULE_BAR_CSS } from "@/components/mobile/KitchenTaskScheduleBar";
import ProductionStageStartBar from "@/components/mobile/ProductionStageStartBar";
import KitchenUnitLabel from "@/components/mobile/KitchenUnitLabel";
import {
  isDecoratorUpstreamReady,
  isProductionStageStarted,
  isRtgDecorSkip,
  mergeProductionTaskInfo,
  postProductionStageStart,
} from "@/lib/productionStageTiming";
import { KITCHEN_SCHEDULE_PHASE_CSS } from "@/components/mobile/KitchenSchedulePhaseRow";
import {
  PRODUCTION_DRIVER_PAGE_CSS,
  MOBILE_CAKE_PROOF_CSS,
} from "@/lib/productionDriverPageStyles";

function readTokenFromQuery() {
  const q = new URLSearchParams(window.location.search);
  return (q.get("token") || "").trim();
}

async function resolveProductionToken() {
  const fromQuery = readTokenFromQuery();
  if (fromQuery) return fromQuery;

  const pathMatch = appPathname().match(/^\/p\/decorator\/([A-Za-z0-9]+)\/?$/i);
  if (!pathMatch) return "";

  const code = pathMatch[1];
  const res = await fetch(`/api/p/decorator/${encodeURIComponent(code)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.redirect_url) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : "Invalid or expired link. Ask manager to resend WhatsApp."
    );
  }
  const u = new URL(data.redirect_url, window.location.origin);
  return (u.searchParams.get("token") || "").trim();
}

function cakeImageSrc(url) {
  if (!url || typeof url !== "string") return url;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return encodeURI(url);
  }
}

function parseProductionError(detail, fallback = "Request failed") {
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || d?.message || String(d)).filter(Boolean).join("; ") || fallback;
  }
  return fallback;
}

async function parseJson(res) {
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseProductionError(j?.detail));
  return j;
}

function decoratorCompleteBlockers({
  upstreamReady,
  beforeCallConfirmed,
  afterCallConfirmed,
  proofSent,
  checks,
  hasFinishedCake,
  hasWeightProof,
  isNewCustomer,
}) {
  const blockers = [];
  if (!upstreamReady) blockers.push("Waiting for froster");
  if (!beforeCallConfirmed) blockers.push("First customer call");
  if (!hasFinishedCake) blockers.push("Design photo");
  if (!hasWeightProof) blockers.push("Weight photo");
  if (!proofSent) blockers.push("Send to customer on WhatsApp");
  if (!afterCallConfirmed) blockers.push("Final customer call");
  if (!checks.assembled) blockers.push("Assembled");
  if (!checks.messaged) blockers.push("Messaged");
  if (!checks.cutlery) blockers.push("Cutlery");
  if (isNewCustomer && !checks.free) blockers.push("Free");
  return blockers;
}

function telHref(tel) {
  const t = (tel || "").trim();
  return t ? `tel:${t}` : null;
}

function formatWeightLabel(weightKg) {
  const w = Number(weightKg);
  if (!Number.isFinite(w) || w <= 0) return null;
  return `${Math.round(w * 1000)} g`;
}

import {
  hubProfileLoginGateMessage,
} from "@/lib/kitchenHubProfile";

function decoratorStageStartGate(info) {
  const loginGate = hubProfileLoginGateMessage();
  if (loginGate) return loginGate;
  if (!info) return "";
  if (isRtgDecorSkip(info)) {
    return "";
  }
  if (info.is_dry_cake || info.dry_cake_skip_froster) {
    if (!info.production_baker_done_at) return "Waiting for baker Done before you can Start.";
  } else if (!info.production_froster_done_at) {
    return "Waiting for froster Done before you can Start.";
  }
  return "";
}

function DecoratorKitchenSchedule({ info }) {
  return <KitchenTaskScheduleBar info={info} role="decorator" done={Boolean(info?.is_done)} />;
}

export default function DecoratorProductionPage({
  hubToken = "",
  hubOrderId = "",
  isHubMode = false,
  onBack,
  onStageComplete,
}) {
  const [accessToken, setAccessToken] = useState(isHubMode ? hubToken : "");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [checks, setChecks] = useState({ assembled: false, messaged: false, cutlery: false, free: false });
  const [finishedCakeUrl, setFinishedCakeUrl] = useState("");
  const [weightProofUrl, setWeightProofUrl] = useState("");
  const [uploadingKind, setUploadingKind] = useState("");
  const [localPreview, setLocalPreview] = useState({ finished_cake: "", weight_proof: "" });
  const [photoInputKey, setPhotoInputKey] = useState({ finished_cake: 0, weight_proof: 0 });
  const [startBusy, setStartBusy] = useState(false);
  const [callStartingPhase, setCallStartingPhase] = useState("");
  const [callCompletingPhase, setCallCompletingPhase] = useState("");
  const [sendingProof, setSendingProof] = useState(false);
  const [refLightbox, setRefLightbox] = useState(false);

  const beforeCallConfirmed = Boolean(info?.customer_call_before?.is_confirmed);
  const afterCallConfirmed = Boolean(info?.customer_call_after?.is_confirmed);
  const designSent = Boolean(info?.decorator_customer_design_sent);
  const proofSent = Boolean(info?.decorator_customer_proof_sent);

  const resolveAccessToken = async () => {
    if (isHubMode && hubToken) return hubToken;
    return accessToken || (await resolveProductionToken());
  };

  const load = async () => {
    const token = await resolveAccessToken();
    if (!token) throw new Error("Link is missing token. Open the full link from WhatsApp.");
    const res = await fetch(
      `/api/production/task?${productionTaskQuery(token, hubOrderId, "decorator", isHubMode)}`
    );
    const data = await parseJson(res);
    setAccessToken(token);
    setInfo((prev) => mergeProductionTaskInfo(prev, data, "decorator"));
    setDone(Boolean(data.is_done));
    setChecks({
      assembled: Boolean(data?.decorator_checks?.assembled),
      messaged: Boolean(data?.decorator_checks?.messaged),
      cutlery: Boolean(data?.decorator_checks?.cutlery),
      free: Boolean(data?.decorator_checks?.free),
    });
    setFinishedCakeUrl((data?.decorator_finished_cake_url || "").trim());
    setWeightProofUrl((data?.decorator_weight_proof_url || "").trim());
  };

  useEffect(() => {
    load().catch((e) => setErr(e.message || String(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (done || !info) return undefined;
    if (isRtgDecorSkip(info) || isDecoratorUpstreamReady(info)) return undefined;
    const poll = () => load().catch(() => {});
    const interval = setInterval(poll, 12000);
    const onVis = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [done, info]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveChecks = async (nextChecks) => {
    const token = await resolveAccessToken();
    const res = await fetch("/api/production/task/decorator-checks", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(productionAuthBody(token, hubOrderId, "decorator", isHubMode, nextChecks)),
    });
    const data = await parseJson(res);
    setInfo(data);
  };

  const dialCustomer = (tel) => {
    const href = (tel || "").trim();
    if (!href) return;
    window.location.href = href.startsWith("tel:") ? href : `tel:${href}`;
  };

  const openCustomerWhatsApp = (phone) => {
    const href = whatsappHref(phone);
    if (!href || href === "#") return;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const onCustomerCallStart = async (phase, channel = "phone") => {
    try {
      setCallStartingPhase(phase);
      const token = await resolveAccessToken();
      const res = await fetch("/api/production/task/customer-call-start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(productionAuthBody(token, hubOrderId, "decorator", isHubMode, { phase })),
      });
      const data = await parseJson(res);
      setInfo(data);
      setErr("");
      if (channel === "whatsapp") {
        openCustomerWhatsApp(data?.customer_phone);
      } else {
        dialCustomer(data?.customer_phone_tel);
      }
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setCallStartingPhase("");
    }
  };

  const onCustomerCallComplete = async (phase) => {
    try {
      setCallCompletingPhase(phase);
      const token = await resolveAccessToken();
      const res = await fetch("/api/production/task/customer-call-complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(productionAuthBody(token, hubOrderId, "decorator", isHubMode, { phase })),
      });
      const data = await parseJson(res);
      setInfo(data);
      setErr("");
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setCallCompletingPhase("");
    }
  };

  const onSendProof = async () => {
    try {
      setSendingProof(true);
      const token = await resolveAccessToken();
      const res = await fetch("/api/production/task/send-decorator-proof-to-customer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(productionAuthBody(token, hubOrderId, "decorator", isHubMode)),
      });
      const data = await parseJson(res);
      setInfo(data);
      setErr("");
    } catch (e) {
      const msg = e.message || String(e);
      if (/timeout|timed out|slow/i.test(msg)) {
        setErr("WhatsApp is slow — wait 5 seconds and tap Send again.");
      } else {
        setErr(msg);
      }
    } finally {
      setSendingProof(false);
    }
  };

  const onToggleCheck = async (key, value) => {
    if (!beforeCallConfirmed) {
      setErr("Complete the first customer call first.");
      return;
    }
    const next = { ...checks, [key]: value };
    setChecks(next);
    try {
      await saveChecks(next);
      setErr("");
    } catch (e) {
      setErr(e.message || String(e));
    }
  };

  const uploadPhoto = async (kind, file) => {
    if (!beforeCallConfirmed) {
      setErr("Complete the first customer call first (tap Call customer, wait 1 min, then Customer call done).");
      return;
    }
    if (!file) {
      setPhotoInputKey((k) => ({ ...k, [kind]: k[kind] + 1 }));
      return;
    }
    setErr("");
    setUploadingKind(kind);
    const preview = URL.createObjectURL(file);
    setLocalPreview((p) => ({ ...p, [kind]: preview }));
    try {
      const { blob, extension } = await compressImageForUpload(file, {
        maxEdge: 1024,
        webpQuality: 0.65,
        jpegQuality: 0.65,
      });
      if (!(blob instanceof Blob) || blob.size <= 0) {
        throw new Error("Could not prepare photo — try Camera instead of Gallery.");
      }
      const token = await resolveAccessToken();
      const fd = productionFormAuth(token, hubOrderId, "decorator", isHubMode, new FormData());
      fd.append("kind", kind);
      fd.append("file", blob, `${kind}.${extension}`);
      const res = await uploadFormWithRetry("/api/production/task/upload-decorator-photo", fd, {
        retries: 3,
        timeoutMs: 60000,
      });
      const data = await parseJson(res);
      const uploadedUrl = (
        kind === "finished_cake"
          ? data.order?.decorator_finished_cake_url || data.url
          : data.order?.decorator_weight_proof_url || data.url
      || "").trim();
      if (!uploadedUrl) {
        throw new Error("Photo saved but server URL missing — try again.");
      }
      if (data.order) {
        setInfo((prev) => mergeProductionTaskInfo(prev, data.order, "decorator"));
      }
      if (kind === "finished_cake") {
        setFinishedCakeUrl(uploadedUrl);
      } else {
        setWeightProofUrl(uploadedUrl);
      }
      URL.revokeObjectURL(preview);
      setLocalPreview((p) => ({ ...p, [kind]: "" }));
      setPhotoInputKey((k) => ({ ...k, [kind]: k[kind] + 1 }));
      setErr("");
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setUploadingKind("");
    }
  };

  const upstreamReady = isDecoratorUpstreamReady(info);
  const rtgDecorSkip = isRtgDecorSkip(info);
  const hasFinishedCake = Boolean((finishedCakeUrl || info?.decorator_finished_cake_url || "").trim());
  const hasWeightProof = Boolean((weightProofUrl || info?.decorator_weight_proof_url || "").trim());
  const canSendToCustomer = upstreamReady && beforeCallConfirmed && hasFinishedCake && hasWeightProof && !proofSent;

  const onStageStart = async () => {
    try {
      setStartBusy(true);
      const token = await resolveAccessToken();
      const order = await postProductionStageStart({
        token,
        hubOrderId,
        role: "decorator",
        isHubMode,
        productionAuthBody,
        parseJson,
      });
      setInfo((prev) => mergeProductionTaskInfo(prev, order, "decorator"));
      setErr("");
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setStartBusy(false);
    }
  };

  const onComplete = async () => {
    if (!isProductionStageStarted(info, "decorator")) {
      setErr("Tap Start at the top when you begin decoration.");
      return;
    }
    if (!upstreamReady) {
      setErr("Froster must tap Done first. This page will update automatically.");
      return;
    }
    const blockers = decoratorCompleteBlockers({
      upstreamReady,
      beforeCallConfirmed,
      afterCallConfirmed,
      proofSent,
      checks,
      hasFinishedCake,
      hasWeightProof,
      isNewCustomer,
    });
    if (blockers.length) {
      setErr(`Before Decorator Done: ${blockers.join(" · ")}`);
      return;
    }
    try {
      setBusy(true);
      const token = await resolveAccessToken();
      const res = await fetch("/api/production/task/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(productionAuthBody(token, hubOrderId, "decorator", isHubMode)),
      });
      const data = await parseJson(res);
      if (data.order) setInfo(data.order);
      setDone(true);
      await load();
      setErr("");
      if (isHubMode && onStageComplete) onStageComplete();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };
  const isNewCustomer = info ? isNewCustomerOrder(info) : false;
  const checklistItems = [
    ["assembled", "Assembled"],
    ["messaged", "Messaged"],
    ["cutlery", "Cutlery"],
    ...(isNewCustomer ? [["free", "Free"]] : []),
  ];
  const completeBlockers = decoratorCompleteBlockers({
    upstreamReady,
    beforeCallConfirmed,
    afterCallConfirmed,
    proofSent,
    checks,
    hasFinishedCake,
    hasWeightProof,
    isNewCustomer,
  });
  const stageStarted = isProductionStageStarted(info, "decorator");
  const stageStartBlocked = decoratorStageStartGate(info);
  const canComplete = completeBlockers.length === 0 && stageStarted;
  const checklistDone = checklistItems.every(([key]) => checks[key]);
  const checklistOutstanding = proofSent && afterCallConfirmed && !checklistDone;

  const helpTel = telHref(info?.help_phone_tel);
  const weightLabel = formatWeightLabel(info?.weight_kg);
  const workUnlocked = upstreamReady && beforeCallConfirmed && !done;

  if (!info && !err) {
    return (
      <div className="driver-page">
        <style>{`${PRODUCTION_DRIVER_PAGE_CSS}${MOBILE_CAKE_PROOF_CSS}${KITCHEN_SCHEDULE_PHASE_CSS}${KITCHEN_TASK_SCHEDULE_BAR_CSS}`}</style>
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}>
          <Loader2 className="h-7 w-7 animate-spin" style={{ color: "#EE2677" }} />
        </div>
      </div>
    );
  }

  return (
    <div className="driver-page">
      <style>{`${PRODUCTION_DRIVER_PAGE_CSS}${MOBILE_CAKE_PROOF_CSS}${KITCHEN_SCHEDULE_PHASE_CSS}${KITCHEN_TASK_SCHEDULE_BAR_CSS}`}</style>

      <MobileTaskHeader title="Decorator" helpTel={helpTel} />

      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            border: "none",
            background: "white",
            borderRadius: 999,
            padding: "10px 14px",
            fontSize: 14,
            fontWeight: 800,
            color: "#EE2677",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            cursor: "pointer",
          }}
        >
          <ArrowLeft size={18} aria-hidden />
          All orders
        </button>
      ) : null}

      {err ? <div className="driver-error">{err}</div> : null}

      {info ? <DecoratorKitchenSchedule info={info} /> : null}

      {info ? (
        <ProductionStageStartBar
          info={info}
          role="decorator"
          done={done}
          busy={startBusy}
          onStart={onStageStart}
          gateMessage={stageStartBlocked}
        />
      ) : null}

      {info?.stage_gate_message && !done ? (
        <div className="driver-success" style={{ background: "#FFF7ED", borderColor: "#FDBA74", color: "#9A3412" }}>
          {info.stage_gate_message}
        </div>
      ) : null}

      {info ? (
        <>
          {!upstreamReady && !rtgDecorSkip ? (
            <div className="driver-card" style={{ opacity: 0.85 }}>
              <div className="driver-label">Waiting for froster</div>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.45, color: "#57534E" }}>
                Froster must finish frosting and tap Done before decoration can start. This page refreshes every few seconds.
              </p>
            </div>
          ) : null}

          {rtgDecorSkip && !done ? (
            <div className="driver-card" style={{ background: "#F0FDF4", borderColor: "#86EFAC" }}>
              <div className="driver-label" style={{ color: "#166534" }}>RTG fridge stock</div>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.45, color: "#166534" }}>
                Pre-frosted cake from fridge — no baker or froster step. Tap Start and decorate when ready.
              </p>
            </div>
          ) : null}

          <div style={!upstreamReady && !rtgDecorSkip ? { opacity: 0.45, pointerEvents: "none" } : undefined}>
          <ProductionCustomerCall
            info={info}
            callInfo={info.customer_call_before}
            phase="before"
            busy={busy}
            done={done}
            starting={callStartingPhase === "before"}
            completing={callCompletingPhase === "before"}
            onStartCall={() => onCustomerCallStart("before", "phone")}
            onStartWhatsAppCall={() => onCustomerCallStart("before", "whatsapp")}
            onCompleteCall={() => onCustomerCallComplete("before")}
            variant="driver"
          />

          {info.cake_image_url ? (
            <div className="driver-card">
              <div className="driver-label">Reference</div>
              <button
                type="button"
                className="driver-cake-proof-hero"
                style={{ marginTop: 8 }}
                onClick={() => setRefLightbox(true)}
              >
                <img
                  src={cakeImageSrc(info.cake_image_url)}
                  alt="Design reference from order"
                />
                <div className="driver-cake-proof-hero-label">
                  Tap to view full size
                </div>
              </button>
            </div>
          ) : null}

          <div style={!workUnlocked ? { opacity: 0.45, pointerEvents: "none" } : undefined}>
            <div className="driver-card">
              <div className="driver-label">Order</div>
              <div className="driver-order-num">#{info.order_number}</div>
              <div className="driver-customer-name">{displayCustomerName(info.customer_name || "Customer")}</div>
              <div className="driver-slot">
                {info.delivery_date || "-"} · {info.delivery_slot || info.custom_time || "-"}
              </div>
            </div>

            {!info.is_cheesecake ? <OrderBakingSpecDisplay info={info} variant="driver" /> : null}

            <div className="driver-card" style={{ padding: "12px 16px" }}>
              <div className="driver-label">Deliver</div>
              <span className="driver-item-pill">
                <Cake size={14} style={{ display: "inline", verticalAlign: "middle" }} />
                {info.flavor || "Cake"}
                {info.weight_kg ? ` · ${info.weight_kg} kg` : ""}
              </span>
              {info.kitchen_schedule?.uses_fondant ? (
                <span className="kitchen-fondant-tag" style={{ marginLeft: 8 }}>
                  Fondant{info.kitchen_schedule.fondant_level ? ` · ${info.kitchen_schedule.fondant_level}` : ""}
                </span>
              ) : null}
              {weightLabel ? (
                <div className="driver-cake-proof-weight" style={{ marginTop: 10 }}>
                  Ordered weight: {weightLabel}
                </div>
              ) : null}
            </div>

            {info.message_on_cake || info.order_notes || info.baker_notes ? (
              <div className="driver-notes-card">
                {info.message_on_cake ? (
                  <p>
                    <strong>Message on cake:</strong> {info.message_on_cake}
                  </p>
                ) : null}
                {info.baker_notes ? (
                  <p style={{ marginTop: 6 }}>
                    <strong>Baker notes:</strong> {info.baker_notes}
                  </p>
                ) : null}
                {info.order_notes ? (
                  <p style={{ marginTop: 6 }}>
                    <strong>Order notes:</strong> {info.order_notes}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div
              className="driver-card"
              style={
                checklistOutstanding
                  ? { border: "2px solid #EE2677", boxShadow: "0 0 0 3px rgba(238,38,119,0.12)" }
                  : undefined
              }
            >
              <div className="driver-label">Decorator checklist</div>
              {isNewCustomer ? (
                <p style={{ margin: "0 0 8px", fontSize: 12, lineHeight: 1.4, color: "#0F766E", fontWeight: 700 }}>
                  NEW customer — include the free gift before Done.
                </p>
              ) : null}
              {checklistOutstanding ? (
                <p style={{ margin: "0 0 10px", fontSize: 13, lineHeight: 1.45, color: "#9D174D", fontWeight: 700 }}>
                  Tap all {checklistItems.length} — required before Decorator Done.
                </p>
              ) : null}
              <div
                className="driver-check-grid"
                style={isNewCustomer ? { gridTemplateColumns: "repeat(2, 1fr)" } : undefined}
              >
                {checklistItems.map(([key, label]) => {
                  const on = checks[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={!workUnlocked}
                      className={`driver-check-pill${on ? " driver-check-pill--on" : ""}`}
                      style={
                        checklistOutstanding && !on
                          ? { borderColor: "#EE2677", color: "#9D174D", fontWeight: 800 }
                          : undefined
                      }
                      onClick={() => onToggleCheck(key, !on)}
                    >
                      {on ? "✓ " : ""}
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="driver-card">
              <div className="driver-label">Design & weight</div>

              <DecoratorPhotoSlot
                title="Design"
                previewUrl={
                  localPreview.finished_cake ||
                  (finishedCakeUrl || info.decorator_finished_cake_url
                    ? cakeImageSrc(finishedCakeUrl || info.decorator_finished_cake_url)
                    : "")
                }
                uploading={uploadingKind === "finished_cake"}
                inputKey={photoInputKey.finished_cake}
                onCapture={(file) => uploadPhoto("finished_cake", file)}
              />

              <DecoratorPhotoSlot
                title="Weight"
                previewUrl={
                  localPreview.weight_proof ||
                  (weightProofUrl || info.decorator_weight_proof_url
                    ? cakeImageSrc(weightProofUrl || info.decorator_weight_proof_url)
                    : "")
                }
                uploading={uploadingKind === "weight_proof"}
                inputKey={photoInputKey.weight_proof}
                onCapture={(file) => uploadPhoto("weight_proof", file)}
              />
            </div>

            <button
              type="button"
              disabled={!canSendToCustomer || sendingProof || busy}
              onClick={onSendProof}
              className="driver-btn-done"
              style={{
                marginBottom: 12,
                background: proofSent ? "#16A34A" : undefined,
                opacity: !canSendToCustomer && !proofSent ? 0.5 : 1,
              }}
            >
              {sendingProof ? "Sending…" : proofSent ? "✓ Sent on WhatsApp" : "Send to customer on WhatsApp"}
            </button>
          </div>

          {proofSent && !afterCallConfirmed && !done ? (
            <ProductionCustomerCall
              info={info}
              callInfo={info.customer_call_after}
              phase="after"
              busy={busy}
              done={done}
              starting={callStartingPhase === "after"}
              completing={callCompletingPhase === "after"}
              onStartCall={() => onCustomerCallStart("after", "phone")}
              onStartWhatsAppCall={() => onCustomerCallStart("after", "whatsapp")}
              onCompleteCall={() => onCustomerCallComplete("after")}
              variant="driver"
            />
          ) : afterCallConfirmed ? (
            <ProductionCustomerCall
              info={info}
              callInfo={info.customer_call_after}
              phase="after"
              busy={busy}
              done={done}
              starting={false}
              completing={false}
              onStartCall={() => {}}
              onCompleteCall={() => {}}
              variant="driver"
            />
          ) : null}
          </div>
        </>
      ) : null}

      {refLightbox && info?.cake_image_url ? (
        <FullWidthImageLightbox
          src={cakeImageSrc(info.cake_image_url)}
          label="Design reference"
          alt="Design reference from order"
          onClose={() => setRefLightbox(false)}
        />
      ) : null}

      {isHubMode && hubOrderId && accessToken && info ? (
        <div style={{ marginBottom: 12 }}>
          <KitchenUnitLabel token={accessToken} orderId={hubOrderId} role="decorator" orderInfo={info} />
        </div>
      ) : null}

      <div className="driver-footer">
        {done ? (
          <div className="driver-success" style={{ justifyContent: "center" }}>
            <CheckCircle2 size={22} />
            <span>Decorator Done — customer notified</span>
          </div>
        ) : (
          <>
            {completeBlockers.length > 0 ? (
              <div
                style={{
                  marginBottom: 10,
                  padding: "10px 12px",
                  borderRadius: 12,
                  background: "#FFF1F2",
                  border: "1px solid #FECDD3",
                  color: "#9D174D",
                  fontSize: 13,
                  lineHeight: 1.45,
                  fontWeight: 700,
                }}
              >
                Still needed: {completeBlockers.join(" · ")}
              </div>
            ) : null}
            <button
              type="button"
              disabled={busy || !stageStarted}
              onClick={onComplete}
              className="driver-btn-done"
              style={{ opacity: canComplete ? 1 : 0.92 }}
            >
              {busy ? "Saving…" : !stageStarted ? "Tap Start first" : "Decorator Done"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
